import { useState } from "react";
import { useTranslation } from "react-i18next";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { AccordionSummary, AccordionDetails, Accordion, Box, Typography, Tooltip } from "@mui/material";

import { AccordionArrowDown } from "../../../../assets/icons/MUI/HomeIcons";

import { ReviewCriteria, reviewCriteriaCategoriesList } from "../../types/candidate";

const DetailedPerformanceRatings = ({ criteria }: { criteria: ReviewCriteria[] }) => {
  const { t } = useTranslation("goHire", { keyPrefix: "candidateReportPage" });
  const { t: tCriteria } = useTranslation("goHire", { keyPrefix: "enum.criteria" });

  const ratings = reviewCriteriaCategoriesList
    .map(({ categoryKey, criteriaKeys }) => {
      const categoryCriteria = criteria.filter((c) => criteriaKeys.includes(c.key));
      if (categoryCriteria.length === 0) return null;

      return {
        title: t(categoryKey),
        skills: categoryCriteria.map((c) => ({
          name: tCriteria(c.key),
          value: c.rating,
          note: c.note,
        })),
      };
    })
    .filter(Boolean) as {
    title: string;
    skills: { name: string; value: number; note: string | null }[];
  }[];

  return (
    <Box sx={styles.container}>
      <Typography sx={styles.title}>{t("detailedPerformanceRatings")}</Typography>
      {ratings.map((rating, index) => (
        <SkillAccordion key={index} rating={rating} />
      ))}
    </Box>
  );
};

export default DetailedPerformanceRatings;

const SkillAccordion = ({
  rating,
}: {
  rating: {
    title: string;
    skills: {
      name: string;
      value: number;
      note: string | null;
    }[];
  };
}) => {
  const { t } = useTranslation("goHire", { keyPrefix: "candidateReportPage" });
  const [expanded, setExpanded] = useState(true);
  const handleChange = () => setExpanded((prev) => !prev);

  return (
    <Box sx={styles.ratingContainer}>
      <Accordion sx={styles.skillAccordion} expanded={expanded} onChange={handleChange}>
        <AccordionSummary expandIcon={<AccordionArrowDown />}>
          <Typography sx={styles.ratingTitle}>{rating.title}</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Box sx={styles.skillsGrid}>
            {rating.skills.map(({ name, value, note }, skillIndex) => (
              <Box key={skillIndex} sx={[styles.skillItemBase, styles.skillItem({ value })]}>
                <Box sx={{ display: "flex", alignItems: "center", gap: "4px" }}>
                  <Typography sx={[styles.SkillNameBase, styles.SkillName({ value })]}>{name}</Typography>
                  {note && (
                    <Tooltip title={note} arrow>
                      <InfoOutlinedIcon
                        sx={{
                          fontSize: "14px",
                          cursor: "pointer",
                          color:
                            value < 3
                              ? "var(--Error-700, #B42318)"
                              : value < 4
                              ? "var(--Warning-700, #B54708)"
                              : "var(--Success-700, #067647)",
                        }}
                      />
                    </Tooltip>
                  )}
                </Box>
                <Typography sx={[styles.SkillValueBase, styles.SkillValue({ value })]}>
                  {t(`scorePercentage`, { score: value, totalScore: 5 })}
                </Typography>
              </Box>
            ))}
          </Box>
        </AccordionDetails>
      </Accordion>
    </Box>
  );
};

const styles = {
  container: {
    display: "flex",
    flexDirection: "column",
    gap: "24px",
    padding: "24px",
    borderRadius: "12px",
    backgroundColor: "var(--Light---100, #F9FAFB)",
    border: "1px solid #EAECF0",
  },
  title: {
    color: "var(--Dark---1000, #111)",
    fontFamily: "Montserrat",
    fontSize: "18px",
    fontStyle: "normal",
    fontWeight: 600,
    lineHeight: "normal",
  },
  ratingContainer: {
    padding: "16px",
    borderRadius: "12px",
    border: "1px solid var(--Gray-200, #EAECF0)",
    background: "var(--White, #FFF)",
  },
  ratingTitle: {
    color: "var(--Dark---800, #1D2939)",
    fontFamily: "Montserrat",
    fontSize: "16px",
    fontWeight: 600,
  },
  skillsGrid: {
    display: "flex",
    flexWrap: "wrap",
    gap: "16px",
  },
  skillItemBase: {
    width: { xs: "100%", sm: "calc(50% - 8px)" },
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "4px 4px 4px 12px",
    borderRadius: "16px",
  },
  skillItem: ({ value }: { value: number }) => ({
    border:
      value < 3
        ? "1px solid var(--Error-200, #FECDCA)"
        : value < 4
        ? "1px solid var(--Warning-200, #FEDF89)"
        : "1px solid var(--Success-200, #ABEFC6)",
    background:
      value < 3 ? "var(--Error-50, #FEF3F2)" : value < 4 ? "var(--Warning-50, #FFFAEB)" : "var(--Success-50, #ECFDF3)",
  }),
  SkillNameBase: {
    color: "var(--Error-700, #B42318)",
    fontFamily: "Inter",
    fontSize: "12px",
    fontStyle: "normal",
    fontWeight: 500,
    lineHeight: "18px",
  },
  SkillName: ({ value }: { value: number }) => ({
    color:
      value < 3
        ? "var(--Error-700, #B42318)"
        : value < 4
        ? "var(--Warning-700, #B54708"
        : "var(--Success-700, #067647)",
  }),
  SkillValueBase: {
    fontFamily: "Montserrat",
    color: "var(--White, #FFF)",
    textAlign: "center",
    fontSize: "12px",
    fontStyle: "normal",
    fontWeight: 500,
    lineHeight: "18px",
    padding: "2px 8px",
    borderRadius: "16px",
    border: "1px solid",
  },
  SkillValue: ({ value }: { value: number }) => ({
    borderColor:
      value < 3
        ? "var(--Error-200, #FECDCA)"
        : value < 4
        ? "var(--Warning-200, #FEDF89)"
        : "var(--Success-200, #ABEFC6)",
    background: value < 3 ? "#B42318" : value < 4 ? "#F5600A" : "#067647",
  }),
  skillAccordion: {
    borderRadius: "8px",
    background: "var(--White, #FFF)",
    boxShadow: "none",
    border: "none",
    "&:before": {
      display: "none",
    },
    "& .MuiAccordionSummary-root": {
      minHeight: "unset",
      padding: "0px",
      "& .MuiAccordionSummary-content": {
        margin: "0px",
      },
      "& .MuiAccordionSummary-expandIconWrapper": {
        margin: "0px",
      },
      "& .MuiAccordionSummary-contentGutters": {
        margin: "0px",
      },
    },
    "& .MuiAccordionDetails-root": {
      p: 0,
      pt: "24px",
    },
  },
};
